import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Flame, Package, Activity, Wind, Settings2,
  TrendingUp, Brain, LogOut, Settings, AlertTriangle
} from "lucide-react";
import { useAuth, usePlant } from "@/lib/context";
import { num, inr, intf, statusColor } from "@/lib/format";
import { useState } from "react";
import AdminPanel from "@/components/AdminPanel";

const NAV = [
  { to: "/", label: "Command", icon: LayoutDashboard, exact: true, testid: "nav-command" },
  { to: "/furnace", label: "Furnace", icon: Flame, testid: "nav-furnace" },
  { to: "/charge", label: "Charge", icon: Package, testid: "nav-charge" },
  { to: "/operations", label: "Operations", icon: Activity, testid: "nav-operations" },
  { to: "/air", label: "Air", icon: Wind, testid: "nav-air" },
  { to: "/utilities", label: "Utilities", icon: Settings2, testid: "nav-utilities" },
  { to: "/yield", label: "Yield", icon: TrendingUp, testid: "nav-yield" },
  { to: "/ai-advisor", label: "AI Advisor", icon: Brain, testid: "nav-ai-advisor" },
];

function StatusDot({ status }) {
  const s = status || "blue";
  return <span className={`dot ${s}`} data-testid={`status-dot-${s}`} />;
}

function KpiTile({ label, value, unit, status, testid, hint }) {
  return (
    <div className={`kpi-tile ${status}`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <span className="label-small">{label}</span>
        <StatusDot status={status} />
      </div>
      <div className="metric-std mt-2" style={{ color: statusColor(status) }} data-testid={`${testid}-value`}>
        {value}
        {unit && <span className="text-[11px] ml-1 text-[#8F93A2] font-display tracking-wider">{unit}</span>}
      </div>
      {hint && <div className="text-[10px] text-[#8F93A2] mt-1 font-display uppercase tracking-wider">{hint}</div>}
    </div>
  );
}

function TopKpiBar({ state }) {
  if (!state) {
    return <div className="topbar h-20 flex items-center px-4">
      <div className="text-[#8F93A2] font-mono text-xs">Loading telemetry...</div>
    </div>;
  }
  const m = state.metrics || {};
  const k = state.kpi_status || {};
  const baseline = state.plant_params?.baseline_sec_target || 620;
  const actualSec = m.ce_07 || 0;
  const secDelta = actualSec - baseline;
  return (
    <div className="h-20 border-b border-[#1F212A] bg-[#0C0D12] flex items-center px-3 gap-2 flex-shrink-0 overflow-x-auto scrollbar-thin" data-testid="top-kpi-bar">
      <KpiTile label="SEC TODAY" value={num(actualSec, 0)} unit="kWh/t" status={k.sec} testid="kpi-sec" hint={`${secDelta >= 0 ? "▲ +" : "▼ "}${num(secDelta, 0)} vs baseline ${num(baseline, 0)}`} />
      <KpiTile label="T2T F1" value={num(state.furnaces?.[0]?.last_tap_to_tap_min, 1)} unit="min" status={k.t2t_f1} testid="kpi-t2t-f1" hint="Target 28" />
      <KpiTile label="T2T F2" value={num(state.furnaces?.[1]?.last_tap_to_tap_min, 1)} unit="min" status={k.t2t_f2} testid="kpi-t2t-f2" hint="Target 28" />
      <KpiTile label="YIELD" value={num(m.ce_22, 1)} unit="%" status={k.yield} testid="kpi-yield" hint="Target 76%" />
      <KpiTile label="₹/TONNE" value={intf(m.ce_30)} unit="INR" status={k.cost} testid="kpi-cost" hint="< ₹5000" />
      <KpiTile label="POWER FACTOR" value={num(state.tags?.MAIN_PF, 3)} status={k.pf} testid="kpi-pf" hint="Target > 0.92" />
    </div>
  );
}

function Sidebar() {
  const { logout, user } = useAuth();
  return (
    <aside className="w-64 h-full border-r border-[#1F212A] bg-[#0C0D12] flex flex-col flex-shrink-0 z-20" data-testid="sidebar">
      <div className="p-5 border-b border-[#1F212A]">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-[#00E5FF] flex items-center justify-center">
            <Flame size={20} strokeWidth={2.2} color="#000" />
          </div>
          <div>
            <div className="font-display font-bold text-[15px] tracking-tight uppercase">Ergoniq</div>
            <div className="font-display text-[10px] uppercase tracking-[0.18em] text-[#8F93A2]">OIM-Foundry</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 scrollbar-thin">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.exact}
            data-testid={n.testid}
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
          >
            <n.icon size={18} strokeWidth={1.6} />
            <span>{n.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-[#1F212A]">
        <div className="text-[10px] uppercase tracking-widest text-[#8F93A2] font-display mb-1">Signed in</div>
        <div className="font-mono text-sm">{user?.username}</div>
        <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] font-display">{user?.role}</div>
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="btn mt-3 w-full flex items-center justify-center gap-2 text-xs"
        >
          <LogOut size={14} /> Sign Out
        </button>
      </div>
    </aside>
  );
}

function Topbar() {
  const { state } = usePlant();
  const [adminOpen, setAdminOpen] = useState(false);
  return (
    <>
      <div className="flex flex-col flex-shrink-0">
        <div className="h-10 border-b border-[#1F212A] bg-[#0C0D12] flex items-center justify-between px-4 flex-shrink-0">
          <div className="economic-chain" data-testid="economic-chain">
            <span>Charge</span><span className="arrow">→</span>
            <span>Furnace</span><span className="arrow">→</span>
            <span>Tap</span><span className="arrow">→</span>
            <span>Mould</span><span className="arrow">→</span>
            <span>Cast</span><span className="arrow">→</span>
            <span>Yield</span><span className="arrow">→</span>
            <span className="text-[#00E5FF]">₹/Tonne</span><span className="arrow">→</span>
            <span>Carbon</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-xs font-mono">
              <span className={`dot ${state?.sim_running ? "green" : "yellow"}`} />
              <span className="font-display uppercase tracking-wider text-[11px] text-[#8F93A2]">
                {state?.mode || "—"}
              </span>
              <span className="text-[#8F93A2]">|</span>
              <span className="text-[#00E5FF]">
                {state?.plant_time ? new Date(state.plant_time).toLocaleString("en-IN", { hour12: false }) : "—"}
              </span>
            </div>
            <button
              className="btn flex items-center gap-2"
              onClick={() => setAdminOpen(true)}
              data-testid="open-admin-panel-btn"
            >
              <Settings size={14} /> Admin
            </button>
          </div>
        </div>
        {state?.simulation_flag && (
          <div className="sim-banner" data-testid="simulation-banner">
            ⚠ Simulation Mode Active — Data shown is physics-generated, not from live plant sensors ⚠
          </div>
        )}
        <TopKpiBar state={state} />
      </div>
      <AdminPanel open={adminOpen} onClose={() => setAdminOpen(false)} />
    </>
  );
}

export default function Shell({ children }) {
  const { state } = usePlant();
  return (
    <div className="h-screen w-full flex bg-[#050507] text-white overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col h-full overflow-hidden min-w-0">
        <Topbar />
        <main className="flex-1 overflow-y-auto p-5 scrollbar-thin" data-testid="page-content">
          {children}
        </main>
      </div>
    </div>
  );
}

export { KpiTile, StatusDot };
